/**
 * API Service for MiniPay
 * 
 * This file contains the API service functions for:
 * 1. Retrieving order information
 * 2. Requesting WeChat payment
 * 3. Querying payment results
 */

import { apiConfig, API_ENDPOINTS } from '../config/apiConfig';

/**
 * Helper function to make API requests
 * 
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - Promise that resolves to API response
 */
const fetchAPI = async (endpoint, options = {}) => {
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    // Add credentials if needed for cookies
    credentials: 'include',
  };

  const fetchOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  // Construct full URL
  const url = `${apiConfig.baseUrl}${endpoint}`;
  
  try {
    const response = await fetch(url, fetchOptions);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }
    
    return data;
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred',
    };
  }
};

/**
 * Fetch order information by order ID
 * 
 * @param {string} orderId - The ID of the order to retrieve
 * @returns {Promise} - Promise that resolves to the order information
 */
export const fetchOrderInfo = async (orderId) => {
  if (!orderId) {
    return {
      success: false,
      error: 'Order ID is required'
    };
  }
  
  return fetchAPI(`${API_ENDPOINTS.ORDER.FETCH_INFO}?orderId=${orderId}`);
};

/**
 * Request WeChat payment for an order
 * 
 * @param {Object} paymentData - Payment request data
 * @param {string} paymentData.orderId - Order ID to pay for
 * @param {number} paymentData.chargeType - Payment type
 * @param {string} paymentData.openid - Optional WeChat openid
 * @returns {Promise} - Promise that resolves to payment request result
 */
export const requestWechatPayment = async (paymentData) => {
  // Validate required fields
  if (!paymentData.orderId) {
    return {
      success: false,
      error: 'Missing required payment information'
    };
  }
  
  return fetchAPI(API_ENDPOINTS.ORDER.REQUEST_PAYMENT, {
    method: 'POST',
    body: JSON.stringify(paymentData)
  });
};

/**
 * Query payment result for an order
 * 
 * @param {string} orderId - Order ID to query payment status for
 * @returns {Promise} - Promise that resolves to payment status
 */
export const queryPaymentResult = async (orderId) => {
  // Validate order ID
  if (!orderId) {
    return {
      success: false,
      error: 'Order ID is required'
    };
  }
  
  return fetchAPI(`${API_ENDPOINTS.ORDER.QUERY_PAYMENT}?orderId=${orderId}`);
};
